import i18next from "i18next";

import { Artefact, ArtefactType } from "@/types/artefacts";

import { DownloadFormat } from "./artefactFormatDetection";

export interface DownloadOption {
  key: string;
  format: DownloadFormat;
  label: string;
  description: string;
  icon: string;
  mimeType: string;
}

/**
 * Format configuration map with localized labels and descriptions
 */
const getFormatOptions = (): Record<DownloadFormat, DownloadOption> => ({
  png: {
    key: "png",
    format: "png",
    label: i18next.t("PNG Image"),
    description: i18next.t("Download as image file"),
    icon: "solar:gallery-bold",
    mimeType: "image/png",
  },
  "pdf-visual": {
    key: "pdf-visual",
    format: "pdf-visual",
    label: i18next.t("PDF (Visual)"),
    description: i18next.t("High-quality visual PDF"),
    icon: "vscode-icons:file-type-pdf2",
    mimeType: "application/pdf",
  },
  "pdf-text": {
    key: "pdf-text",
    format: "pdf-text",
    label: i18next.t("PDF (Text)"),
    description: i18next.t("Selectable text PDF"),
    icon: "vscode-icons:file-type-pdf2",
    mimeType: "application/pdf",
  },
  excel: {
    key: "excel",
    format: "excel",
    label: i18next.t("Excel Spreadsheet"),
    description: i18next.t("Export data to Excel"),
    icon: "vscode-icons:file-type-excel",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  csv: {
    key: "csv",
    format: "csv",
    label: i18next.t("CSV File"),
    description: i18next.t("Export as comma-separated values"),
    icon: "solar:file-text-bold",
    mimeType: "text/csv",
  },
  word: {
    key: "word",
    format: "word",
    label: i18next.t("Word Document"),
    description: i18next.t("Export to Word document"),
    icon: "vscode-icons:file-type-word",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  "word-visual": {
    key: "word-visual",
    format: "word-visual",
    label: i18next.t("Word Document (Visual)"),
    description: i18next.t("High-quality visual Word export"),
    icon: "vscode-icons:file-type-word",
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  powerpoint: {
    key: "powerpoint",
    format: "powerpoint",
    label: i18next.t("PowerPoint Presentation"),
    description: i18next.t("Export to editable PowerPoint slides"),
    icon: "vscode-icons:file-type-powerpoint",
    mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  "powerpoint-visual": {
    key: "powerpoint-visual",
    format: "powerpoint-visual",
    label: i18next.t("PowerPoint (Visual)"),
    description: i18next.t("Export as screenshot in PowerPoint"),
    icon: "vscode-icons:file-type-powerpoint",
    mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  "html-source": {
    key: "html-source",
    format: "html-source",
    label: i18next.t("HTML Source"),
    description: i18next.t("Download HTML source code"),
    icon: "solar:code-bold",
    mimeType: "text/html",
  },
  "html-interactive": {
    key: "html-interactive",
    format: "html-interactive",
    label: i18next.t("Interactive HTML"),
    description: i18next.t("Standalone interactive HTML"),
    icon: "solar:widget-bold",
    mimeType: "text/html",
  },
  markdown: {
    key: "markdown",
    format: "markdown",
    label: i18next.t("Markdown File"),
    description: i18next.t("Export as Markdown"),
    icon: "solar:document-bold",
    mimeType: "text/markdown",
  },
  text: {
    key: "text",
    format: "text",
    label: i18next.t("Text File"),
    description: i18next.t("Export as plain text"),
    icon: "solar:file-text-bold",
    mimeType: "text/plain",
  },
});

/**
 * Get available download options for an artefact
 * Returns standardized export formats based on artefact type
 * @param artefact - The artefact to get download options for
 * @param showAdvanced - If true, shows additional formats like PNG, CSV, Markdown, HTML, Text
 */
export const getDownloadOptions = (
  artefact: Artefact,
  showAdvanced: boolean = false,
): DownloadOption[] => {
  const formatOptions = getFormatOptions();

  // For TABLE artefacts: Excel (data), PowerPoint (data), Word (data), PDF (visual)
  if (artefact.type === ArtefactType.TABLE) {
    const options = [
      formatOptions.excel,
      formatOptions.powerpoint,
      formatOptions.word,
      formatOptions["pdf-visual"],
    ];

    if (showAdvanced) {
      options.push(
        formatOptions.csv,
        formatOptions.png,
        formatOptions.markdown,
        formatOptions.text,
      );
    }

    return options;
  }

  // For HTML artefacts (charts, visualizations): Excel, PowerPoint (visual), Word (visual), PDF (visual)
  if (artefact.type === ArtefactType.HTML) {
    const options = [
      formatOptions.excel,
      formatOptions["powerpoint-visual"],
      formatOptions["word-visual"],
      formatOptions["pdf-visual"],
    ];

    if (showAdvanced) {
      options.push(
        formatOptions.png,
        formatOptions["html-source"],
        formatOptions["html-interactive"],
      );
    }

    return options;
  }

  // For CODE artefacts: Default to standard text-based formats
  if (artefact.type === ArtefactType.CODE) {
    const options = [
      formatOptions.word,
      formatOptions.excel,
      formatOptions.powerpoint,
      formatOptions["pdf-text"],
    ];

    if (showAdvanced) {
      options.push(
        formatOptions.png,
        formatOptions["pdf-visual"],
        formatOptions.markdown,
        formatOptions.text,
      );
    }

    return options;
  }

  // For all other artefacts (DOCUMENT, etc.): Word (text), Excel, PowerPoint, PDF (text)
  const options = [
    formatOptions.word,
    formatOptions.excel,
    formatOptions.powerpoint,
    formatOptions["pdf-text"],
  ];

  if (showAdvanced) {
    // For DOCUMENT, "Text File" is redundant with "Markdown File" (same content), so omit it
    if (artefact.type === ArtefactType.DOCUMENT) {
      options.push(formatOptions.markdown);
    } else {
      options.push(formatOptions.markdown, formatOptions.text);
    }
  }

  return options;
};

/**
 * Get download option by format
 */
export const getDownloadOption = (format: DownloadFormat): DownloadOption => {
  return getFormatOptions()[format];
};

/**
 * Get file extension for a format
 */
export const getFileExtension = (format: DownloadFormat): string => {
  const extensionMap: Record<DownloadFormat, string> = {
    png: "png",
    "pdf-visual": "pdf",
    "pdf-text": "pdf",
    excel: "xlsx",
    csv: "csv",
    word: "docx",
    "word-visual": "docx",
    powerpoint: "pptx",
    "powerpoint-visual": "pptx",
    "html-source": "html",
    "html-interactive": "html",
    markdown: "md",
    text: "txt",
  };

  return extensionMap[format];
};

/**
 * Get user-friendly format name for display
 */
export const getFormatDisplayName = (format: DownloadFormat): string => {
  return getFormatOptions()[format].label;
};
